import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Assessment() {
  // SEO Configuration
  useSEO({
    title: 'Care Assessment - Find Your Ideal Healthcare Provider',
    description: 'Take our comprehensive care assessment to get matched with the perfect healthcare provider. Personalized recommendations based on your specific needs and preferences in Las Vegas.',
    keywords: 'care assessment, healthcare matching, personalized care recommendations, home health evaluation, provider matching Las Vegas',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "name": "Personalized Care Assessment",
          "description": "Comprehensive assessment to match patients with ideal healthcare providers",
          "provider": getOrganizationSchema()
        },
        {
          "@type": "Service",
          "name": "Personalized Care Assessment",
          "serviceType": "Healthcare Provider Matching",
          "description": "Comprehensive assessment and matching service to connect patients with the ideal home healthcare provider in Las Vegas",
          "provider": getOrganizationSchema(),
          "areaServed": { "@type": "State", "name": "Nevada" }
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Care Assessment', url: '/assessment' }
        ])
      ]
    }
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [assessmentData, setAssessmentData] = useState({
    // Basic Information
    patientAge: '',
    livingArrangement: '',
    mobilityLevel: '',
    cognitiveStatus: '',
    
    // Care Needs
    medicalConditions: [],
    dailyActivities: [],
    specialNeeds: [],
    
    // Preferences
    carePreferences: [],
    schedulePreferences: '',
    budgetRange: '',
    insuranceType: '',
    
    // Location & Logistics
    location: '',
    transportationNeeds: false,
    emergencyContacts: true
  });

  const [recommendations, setRecommendations] = useState(null);

  const totalSteps = 4;

  const medicalConditions = [
    'Diabetes', 'Heart Disease', 'Stroke Recovery', 'COPD', 'Arthritis',
    'Alzheimer\'s/Dementia', 'Parkinson\'s', 'Cancer', 'Kidney Disease',
    'Mental Health Conditions', 'Chronic Pain', 'Wound Care Needs'
  ];

  const dailyActivities = [
    'Bathing/Showering', 'Dressing', 'Meal Preparation', 'Medication Management',
    'Light Housekeeping', 'Transportation', 'Grocery Shopping', 'Companionship',
    'Exercise/Physical Therapy', 'Medical Appointments'
  ];

  const specialNeeds = [
    'IV Therapy', 'Wound Care', 'Catheter Care', 'Oxygen Therapy',
    'Physical Therapy', 'Speech Therapy', 'Occupational Therapy',
    'Memory Care', 'Respite Care', '24/7 Supervision'
  ];

  const carePreferences = [
    'Same caregiver consistently', 'Bilingual caregiver', 'Female caregiver',
    'Male caregiver', 'Pet-friendly caregiver', 'Non-smoking caregiver',
    'Experienced with specific conditions', 'Flexible scheduling'
  ];

  const handleInputChange = (field, value) => {
    setAssessmentData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const generateRecommendations = () => {
    const { medicalConditions, specialNeeds, dailyActivities, cognitiveStatus, mobilityLevel } = assessmentData;
    
    const agencies = [];
    
    // Med-Care Providers - for medical conditions and skilled care
    if (medicalConditions.some(condition => 
      ['Diabetes', 'Heart Disease', 'Mental Health Conditions', 'Chronic Pain'].includes(condition)
    ) || specialNeeds.includes('IV Therapy')) {
      agencies.push({
        name: 'Med-Care Providers',
        match: 95,
        reasons: [
          'Primary care and psychiatric services available',
          'Specialized pain management programs',
          'Home clinical visits for medical conditions',
          'Comprehensive medical care coordination'
        ],
        services: ['Primary Care', 'Psychiatric Services', 'Pain Management', 'Home Medical Visits'],
        color: 'blue'
      });
    }

    // Celebrity Home Health - for skilled nursing and therapy
    if (specialNeeds.some(need => 
      ['IV Therapy', 'Wound Care', 'Physical Therapy', 'Speech Therapy', 'Occupational Therapy'].includes(need)
    ) || medicalConditions.includes('Stroke Recovery')) {
      agencies.push({
        name: 'Celebrity Home Health',
        match: 92,
        reasons: [
          'Medicare-certified skilled nursing services',
          'Comprehensive rehabilitation therapy programs',
          'Advanced wound care and IV therapy',
          'Post-surgical and stroke recovery specialists'
        ],
        services: ['Skilled Nursing', 'Physical Therapy', 'IV Therapy', 'Wound Care'],
        color: 'teal'
      });
    }

    // Accent Senior Care - for daily living assistance
    if (dailyActivities.some(activity => 
      ['Bathing/Showering', 'Meal Preparation', 'Light Housekeeping', 'Companionship'].includes(activity)
    ) && !specialNeeds.some(need => ['IV Therapy', 'Wound Care'].includes(need))) {
      agencies.push({
        name: 'Accent Senior Care',
        match: 88,
        reasons: [
          'Reliable daily living assistance',
          'Medicaid waiver approved services',
          'Flexible care plans and scheduling',
          'Background-checked, CNA-trained caregivers'
        ],
        services: ['Personal Care', 'Companion Care', 'Light Housekeeping', 'Meal Preparation'],
        color: 'green'
      });
    }

    // A Peace of Mind Caregivers - for memory care and specialized needs
    if (medicalConditions.includes('Alzheimer\'s/Dementia') || 
        cognitiveStatus === 'mild-impairment' || 
        cognitiveStatus === 'moderate-impairment' ||
        specialNeeds.includes('Memory Care')) {
      agencies.push({
        name: 'A Peace of Mind Caregivers',
        match: 96,
        reasons: [
          'Specialized Alzheimer\'s and dementia care',
          'Compassion-first training approach',
          'Memory support and cognitive activities',
          'Respite care services for family caregivers'
        ],
        services: ['Memory Care', 'Alzheimer\'s Support', 'Respite Care', 'Specialized Personal Care'],
        color: 'purple'
      });
    }

    // Sort by match percentage
    agencies.sort((a, b) => b.match - a.match);

    setRecommendations({
      topMatch: agencies[0],
      allMatches: agencies,
      careLevel: specialNeeds.length > 2 ? 'High' : dailyActivities.length > 4 ? 'Moderate' : 'Basic',
      estimatedCost: assessmentData.insuranceType === 'Medicare' ? '$0-50/month' : 
                    assessmentData.insuranceType === 'Medicaid Waiver' ? '$0-100/month' : 
                    '$2000-4000/month'
    });
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      generateRecommendations();
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700 text-white py-24 pt-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/77ffe14d803497d903570691891c6f9e.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg">
                🎯 Personalized Care Matching
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-8">
              Find Your Ideal Healthcare Provider
            </h1>
            <p className="text-2xl text-purple-100 max-w-4xl mx-auto leading-relaxed">
              Take our comprehensive care assessment to get matched with the perfect healthcare provider based on your specific needs and preferences.
            </p>
          </div>
        </div>
      </section>

      {!recommendations ? (
        <>
          {/* Progress Bar */}
          <section className="py-8 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-semibold text-gray-700">Step {currentStep} of {totalSteps}</span>
                <span className="text-lg font-semibold text-purple-600">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-purple-600 to-blue-600 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>
          </section>

          {/* Assessment Form */}
          <section className="py-24 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-white rounded-3xl shadow-2xl p-12 border border-gray-100">
                
                {/* Step 1: Basic Information */}
                {currentStep === 1 && (
                  <div>
                    <div className="text-center mb-12">
                      <h2 className="text-4xl font-bold text-gray-900 mb-6">Basic Information</h2>
                      <p className="text-xl text-gray-600">Tell us about the patient's current situation</p>
                    </div>

                    <div className="space-y-10">
                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Patient Age Range</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {['Under 65', '65-75', '76-85', '85+'].map((age) => (
                            <button
                              key={age}
                              type="button"
                              onClick={() => handleInputChange('patientAge', age)}
                              className={`p-6 rounded-xl border-2 font-semibold text-lg transition-all cursor-pointer ${
                                assessmentData.patientAge === age
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {age}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Living Arrangement</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {['Lives alone', 'Lives with family', 'Lives with spouse', 'Assisted living'].map((arrangement) => (
                            <button
                              key={arrangement}
                              type="button"
                              onClick={() => handleInputChange('livingArrangement', arrangement)}
                              className={`p-6 rounded-xl border-2 font-semibold text-lg transition-all cursor-pointer ${
                                assessmentData.livingArrangement === arrangement
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {arrangement}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Mobility Level</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {[
                            { value: 'independent', label: 'Independent' },
                            { value: 'assistance-needed', label: 'Some Assistance Needed' },
                            { value: 'wheelchair-bound', label: 'Wheelchair/Bed Bound' }
                          ].map((mobility) => (
                            <button
                              key={mobility.value}
                              type="button"
                              onClick={() => handleInputChange('mobilityLevel', mobility.value)}
                              className={`p-6 rounded-xl border-2 font-semibold text-lg transition-all cursor-pointer ${
                                assessmentData.mobilityLevel === mobility.value
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {mobility.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Cognitive Status</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[
                            { value: 'normal', label: 'Normal Cognitive Function' },
                            { value: 'mild-impairment', label: 'Mild Cognitive Impairment' },
                            { value: 'moderate-impairment', label: 'Moderate Impairment' },
                            { value: 'severe-impairment', label: 'Severe Impairment' }
                          ].map((cognitive) => (
                            <button
                              key={cognitive.value}
                              type="button"
                              onClick={() => handleInputChange('cognitiveStatus', cognitive.value)}
                              className={`p-6 rounded-xl border-2 font-semibold text-lg transition-all cursor-pointer ${
                                assessmentData.cognitiveStatus === cognitive.value
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {cognitive.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Care Needs */}
                {currentStep === 2 && (
                  <div>
                    <div className="text-center mb-12">
                      <h2 className="text-4xl font-bold text-gray-900 mb-6">Care Needs Assessment</h2>
                      <p className="text-xl text-gray-600">Select all that apply to the patient's situation</p>
                    </div>

                    <div className="space-y-12">
                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Medical Conditions</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {medicalConditions.map((condition) => (
                            <button
                              key={condition}
                              type="button"
                              onClick={() => handleMultiSelect('medicalConditions', condition)}
                              className={`p-4 rounded-xl border-2 font-medium text-base transition-all cursor-pointer ${
                                assessmentData.medicalConditions.includes(condition)
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {condition}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Daily Activities Needing Assistance</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {dailyActivities.map((activity) => (
                            <button
                              key={activity}
                              type="button"
                              onClick={() => handleMultiSelect('dailyActivities', activity)}
                              className={`p-4 rounded-xl border-2 font-medium text-base transition-all cursor-pointer ${
                                assessmentData.dailyActivities.includes(activity)
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {activity}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Special Medical Needs</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {specialNeeds.map((need) => (
                            <button
                              key={need}
                              type="button"
                              onClick={() => handleMultiSelect('specialNeeds', need)}
                              className={`p-4 rounded-xl border-2 font-medium text-base transition-all cursor-pointer ${
                                assessmentData.specialNeeds.includes(need)
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {need}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Preferences */}
                {currentStep === 3 && (
                  <div>
                    <div className="text-center mb-12">
                      <h2 className="text-4xl font-bold text-gray-900 mb-6">Care Preferences</h2>
                      <p className="text-xl text-gray-600">Help us match you with the right caregiver</p>
                    </div>

                    <div className="space-y-10">
                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Caregiver Preferences</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {carePreferences.map((preference) => (
                            <button
                              key={preference}
                              type="button"
                              onClick={() => handleMultiSelect('carePreferences', preference)}
                              className={`p-4 rounded-xl border-2 font-medium text-base transition-all cursor-pointer ${
                                assessmentData.carePreferences.includes(preference)
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {preference}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Preferred Schedule</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[
                            'Weekdays only',
                            'Weekends included',
                            'Evenings preferred',
                            'Overnight care needed',
                            'Flexible scheduling',
                            '24/7 care needed'
                          ].map((schedule) => (
                            <button
                              key={schedule}
                              type="button"
                              onClick={() => handleInputChange('schedulePreferences', schedule)}
                              className={`p-4 rounded-xl border-2 font-medium text-base transition-all cursor-pointer ${
                                assessmentData.schedulePreferences === schedule
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {schedule}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Budget Range (Monthly)</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {[
                            'Under $1,000',
                            '$1,000 - $2,500',
                            '$2,500 - $5,000',
                            '$5,000 - $8,000',
                            'Over $8,000',
                            'Insurance covered'
                          ].map((budget) => (
                            <button
                              key={budget}
                              type="button"
                              onClick={() => handleInputChange('budgetRange', budget)}
                              className={`p-4 rounded-xl border-2 font-medium text-base transition-all cursor-pointer ${
                                assessmentData.budgetRange === budget
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {budget}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Final Details */}
                {currentStep === 4 && (
                  <div>
                    <div className="text-center mb-12">
                      <h2 className="text-4xl font-bold text-gray-900 mb-6">Final Details</h2>
                      <p className="text-xl text-gray-600">Just a few more details to complete your assessment</p>
                    </div>

                    <div className="space-y-10">
                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Insurance Type</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {['Medicare', 'Medicaid Waiver', 'Private Insurance', 'Private Pay', 'Other'].map((insurance) => (
                            <button
                              key={insurance}
                              type="button"
                              onClick={() => handleInputChange('insuranceType', insurance)}
                              className={`p-4 rounded-xl border-2 font-medium text-base transition-all cursor-pointer ${
                                assessmentData.insuranceType === insurance
                                  ? 'border-purple-500 bg-purple-50 text-purple-700'
                                  : 'border-gray-300 hover:border-purple-300 text-gray-700'
                              }`}
                            >
                              {insurance}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block font-bold text-gray-700 mb-6 text-xl">Location (City)</label>
                        <input
                          type="text"
                          value={assessmentData.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                          placeholder="Enter city (e.g., Las Vegas, Reno, Henderson)"
                        />
                      </div>

                      <div className="space-y-6">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={assessmentData.transportationNeeds}
                            onChange={(e) => handleInputChange('transportationNeeds', e.target.checked)}
                            className="mr-4 h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <label className="text-lg font-medium text-gray-700">
                            Transportation to medical appointments needed
                          </label>
                        </div>

                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={assessmentData.emergencyContacts}
                            onChange={(e) => handleInputChange('emergencyContacts', e.target.checked)}
                            className="mr-4 h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <label className="text-lg font-medium text-gray-700">
                            Emergency contacts are available and updated
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-16">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className="whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Previous
                  </Button>

                  <Button
                    size="lg"
                    onClick={nextStep}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 whitespace-nowrap"
                  >
                    {currentStep === totalSteps ? (
                      <>
                        <i className="ri-search-line mr-2"></i>
                        Get My Recommendations
                      </>
                    ) : (
                      <>
                        Next
                        <i className="ri-arrow-right-line ml-2"></i>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </>
      ) : (
        /* Recommendations Results */
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-8">Your Personalized Care Recommendations</h2>
              <p className="text-2xl text-gray-600">Based on your assessment, here are your best matches</p>
            </div>

            {/* Top Match */}
            <div className="mb-16">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-3xl p-12 border-2 border-purple-200">
                <div className="text-center mb-10">
                  <span className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-full text-xl font-bold shadow-lg">
                    🏆 TOP MATCH - {recommendations.topMatch.match}% Compatible
                  </span>
                </div>
                
                <div className="bg-white rounded-2xl p-10 shadow-xl">
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-4xl font-bold text-gray-900">{recommendations.topMatch.name}</h3>
                    <span className={`w-6 h-6 rounded-full bg-${recommendations.topMatch.color}-500 shadow-lg`}></span>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-6">Why This Is Your Best Match:</h4>
                      <ul className="space-y-4">
                        {recommendations.topMatch.reasons.map((reason, index) => (
                          <li key={index} className="flex items-start">
                            <i className="ri-check-line text-green-600 mr-4 mt-1 text-xl bg-green-100 rounded-full p-1"></i>
                            <span className="text-gray-700 text-lg">{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-2xl font-bold text-gray-900 mb-6">Key Services:</h4>
                      <div className="flex flex-wrap gap-3">
                        {recommendations.topMatch.services.map((service, index) => (
                          <span key={index} className={`bg-${recommendations.topMatch.color}-100 text-${recommendations.topMatch.color}-800 px-6 py-3 rounded-full font-semibold`}>
                            {service}
                          </span>
                        ))}
                      </div>
                      
                      <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                        <div className="grid grid-cols-2 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-gray-900">Care Level</div>
                            <div className="text-lg text-gray-600">{recommendations.careLevel}</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-gray-900">Est. Cost</div>
                            <div className="text-lg text-gray-600">{recommendations.estimatedCost}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center mt-10">
                    <Link to="/referral">
                      <Button size="lg" className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 whitespace-nowrap text-xl px-12 py-4 shadow-xl">
                        <i className="ri-file-text-line mr-3"></i>
                        Start Referral with {recommendations.topMatch.name}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* All Matches */}
            <div className="mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-10 text-center">All Compatible Providers</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {recommendations.allMatches.map((agency, index) => (
                  <div key={index} className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="text-2xl font-bold text-gray-900">{agency.name}</h4>
                      <div className="flex items-center">
                        <span className={`w-4 h-4 rounded-full bg-${agency.color}-500 mr-3`}></span>
                        <span className="text-xl font-bold text-gray-600">{agency.match}% Match</span>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className={`bg-${agency.color}-500 h-3 rounded-full`}
                          style={{ width: `${agency.match}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {agency.reasons.slice(0, 2).map((reason, reasonIndex) => (
                        <li key={reasonIndex} className="flex items-start">
                          <i className="ri-check-line text-green-600 mr-3 mt-1 text-lg"></i>
                          <span className="text-gray-700">{reason}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="flex flex-wrap gap-2">
                      {agency.services.slice(0, 3).map((service, serviceIndex) => (
                        <span key={serviceIndex} className={`bg-${agency.color}-100 text-${agency.color}-800 px-4 py-2 rounded-full text-sm font-medium`}>
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Steps */}
            <div className="text-center">
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-12 text-white">
                <h3 className="text-3xl font-bold mb-6">Ready to Get Started?</h3>
                <p className="text-xl text-purple-100 mb-10 max-w-3xl mx-auto">
                  Our care coordinators will help you connect with your matched providers and handle all the details.
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link to="/referral">
                    <Button variant="secondary" size="lg" className="bg-white text-purple-600 hover:bg-purple-50 whitespace-nowrap">
                      <i className="ri-file-text-line mr-3"></i>
                      Submit Referral
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-white/10 whitespace-nowrap cursor-pointer"
                    onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                  >
                    <i className="ri-customer-service-line mr-3"></i>
                    Talk to Care Coordinator
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}